/**
 * Summary — the window at a glance: what came in, what was stopped, and how
 * much fraud got through. Data: GET /analytics/tt/summary and /blocked.
 */
import React, { useEffect, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Activity, Ban, CircleCheck, CreditCard, Lock, ShieldCheck, Shuffle, TriangleAlert, UserCheck } from "lucide-react";
import { useTTQuery } from "../ttApi";
import { bucketLabel, card, count, dateTime, friendlyReasons, money, moneyExact, shortId } from "../ttFormat";
import {
  AccountLinks,
  ChartBox,
  INK,
  LoadState,
  OUTCOME_COLORS,
  Pager,
  Pill,
  RankBars,
  ReviewCard,
  ReviewGrid,
  Table,
  Tile,
  Tiles,
  axisProps,
} from "../ttKit";

const PAGE_SIZE = 15;

export default function SummaryPanel({ win, fresh, openAccount }) {
  const summary = useTTQuery("summary", { window: win, fresh });
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [win]);
  const blocked = useTTQuery("blocked", { window: win, page, limit: PAGE_SIZE, fresh });

  return (
    <>
      <LoadState state={summary} height={260}>
        {(d) => <SummaryBody data={d} />}
      </LoadState>

      <ReviewCard
        eyebrow="Prevention"
        title="Blocked transactions"
        desc="Stopped by Idenza before they reached a bank, so they never become a chargeback."
        tag="Prevented"
        tagTone="green"
      >
        <LoadState state={blocked}>
          {(b) => (
            <>
              <Table
                emptyState="No blocked attempts in this window."
                columns={BLOCKED_COLUMNS(openAccount)}
                rows={b.rows.map((r) => ({ key: r.request_id, ...r }))}
              />
              <Pager page={page} limit={PAGE_SIZE} total={b.total} onPage={setPage} />
            </>
          )}
        </LoadState>
      </ReviewCard>
    </>
  );
}

function SummaryBody({ data }) {
  const k = data.kpis || {};
  const lists = data.lists || {};
  const bucket = data.window?.bucket;
  const activity = (data.activity || []).map((a) => ({
    label: bucketLabel(a.bucket, bucket),
    ...a,
    abandoned: Math.max(0, a.attempts - a.completed - a.fraud_declines - a.other_declines - a.blocked),
  }));

  return (
    <>
      <Tiles>
        <Tile
          tone="blue"
          icon={Activity}
          label="Deposits scored"
          value={money(k.scored_usd)}
          sub={`${count(k.scored_n)} attempts · ${count(k.accounts)} accounts`}
        />
        <Tile tone="green" icon={CircleCheck} label="Completed" value={money(k.completed_usd)} sub={`${count(k.completed_n)} deposits`} />
        <Tile
          tone="green"
          icon={ShieldCheck}
          label="Blocked before reaching a bank"
          value={money(k.blocked_usd)}
          sub={`${count(k.blocked_n)} attempts, never fraud coded`}
        />
        <Tile
          tone="red"
          icon={TriangleAlert}
          label="Issuer fraud declines"
          value={money(k.fraud_usd)}
          sub={`${count(k.fraud_n)} attempts · ${count(k.fraud_accounts)} accounts · ${count(k.fraud_cards)} cards`}
        />
        <Tile
          tone="amber"
          icon={CreditCard}
          label="Completed on cards later refused for fraud"
          value={money(k.completed_usd_card_later_fd)}
          sub={`${count(k.completed_n_card_later_fd)} deposits · likely chargebacks`}
        />
      </Tiles>

      <Tiles>
        <Tile tone="amber" icon={Shuffle} label="Cards used on more than one account" value={count(k.shared_cards)} sub="In this window" />
        <Tile tone="red" icon={Ban} label="Blacklisted accounts" value={count(lists.blacklisted)} sub="Current list" />
        <Tile tone="green" icon={UserCheck} label="Whitelisted accounts" value={count(lists.whitelisted)} sub="Current list" />
        <Tile tone="blue" icon={Lock} label="Forced to 3DS" value={count(lists.forced_3ds)} sub="Current list" />
      </Tiles>

      <ReviewGrid>
        <ReviewCard eyebrow="Activity" title="What happened to each attempt" desc={bucket === "hour" ? "Per hour." : "Per day."}>
          <ChartBox height={280}>
            <ResponsiveContainer>
              <ComposedChart data={activity} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={INK.line} />
                <XAxis dataKey="label" {...axisProps} minTickGap={16} />
                <YAxis {...axisProps} allowDecimals={false} width={40} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="completed" name="Completed" stackId="o" fill={OUTCOME_COLORS.completed} />
                <Bar dataKey="fraud_declines" name="Fraud declines" stackId="o" fill={OUTCOME_COLORS.fraud_declines} />
                <Bar dataKey="other_declines" name="Other declines" stackId="o" fill={OUTCOME_COLORS.other_declines} />
                <Bar dataKey="blocked" name="Blocked by Idenza" stackId="o" fill={OUTCOME_COLORS.blocked} />
                <Bar dataKey="abandoned" name="Abandoned" stackId="o" fill={OUTCOME_COLORS.abandoned} />
                <Line dataKey="attempts" name="Attempts" type="monotone" stroke={OUTCOME_COLORS.attempts} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartBox>
        </ReviewCard>

        <ReviewCard eyebrow="Rules" title="Top rules fired" desc="v3 rules with non-zero weight.">
          <RankBars rows={data.top_rules} label="rule" value="hits" />
        </ReviewCard>
      </ReviewGrid>
    </>
  );
}

const BLOCKED_COLUMNS = (openAccount) => [
  { key: "scored_at", label: "Time", render: (r) => dateTime(r.scored_at) },
  { key: "order_key", label: "Order ID", render: (r) => <span title={r.order_key}>{shortId(r.order_key, 18)}</span> },
  { key: "email", label: "Account", render: (r) => <AccountLinks emails={[r.email]} onOpen={openAccount} /> },
  { key: "amount", label: "Amount", numeric: true, render: (r) => moneyExact(r.amount) },
  { key: "card", label: "Card", render: (r) => card(r.bin, r.last4) },
  { key: "reasons", label: "Reasons", render: (r) => <span title={(r.rules_fired || []).join(", ")}>{friendlyReasons(r.rules_fired, r.enforced)}</span> },
  {
    key: "mode",
    label: "Traffic",
    render: (r) => <Pill tone={r.mode === "live" ? "blue" : "slate"}>{r.mode === "live" ? "Enforced" : "Unenforced"}</Pill>,
  },
];
